import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton } from "../../../design-system";
import { assetService } from "../../../services/api";
import { ASSET_CONDITIONS, fieldLabel, inputSm } from "./constants";

const emptyForm = {
  name: "",
  categoryId: "",
  typeId: "",
  serialNumber: "",
  quantity: 1,
  purchaseDate: "",
  purchaseValue: "",
  vendor: "",
  branchId: "",
  departmentId: "",
  location: "",
  condition: "good",
  warrantyExpiry: "",
  amcExpiry: "",
  notes: "",
  isSerialized: true,
};

export default function AssetFormModal({
  open,
  asset,
  categories = [],
  types = [],
  onClose,
  onSaved,
}) {
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const isEdit = Boolean(asset?.id);

  useEffect(() => {
    if (!open) return;
    if (asset) {
      setForm({
        name: asset.name || "",
        categoryId: asset.categoryId || asset.category_id || "",
        typeId: asset.typeId || asset.type_id || "",
        serialNumber: asset.serialNumber || asset.serial_number || "",
        quantity: asset.quantity ?? asset.quantity_total ?? 1,
        purchaseDate: (asset.purchaseDate || asset.purchase_date || "").slice(0, 10),
        purchaseValue: asset.purchaseValue ?? asset.purchase_value ?? "",
        vendor: asset.vendor || "",
        branchId: asset.branchId || asset.branch_id || "",
        departmentId: asset.departmentId || asset.department_id || "",
        location: asset.location || "",
        condition: asset.condition || "good",
        warrantyExpiry: (asset.warrantyExpiry || asset.warranty_expiry || "").slice(0, 10),
        amcExpiry: (asset.amcExpiry || asset.amc_expiry || "").slice(0, 10),
        notes: asset.notes || "",
        isSerialized: asset.isSerialized ?? asset.is_serialized ?? Boolean(asset.serialNumber),
      });
    } else {
      setForm(emptyForm);
    }
  }, [open, asset]);

  if (!open) return null;

  const set = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const filteredTypes = types.filter((t) => {
    if (!form.categoryId) return true;
    const catId = t.categoryId || t.category_id;
    return !catId || String(catId) === String(form.categoryId);
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Asset name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity) || 1,
        purchaseValue: form.purchaseValue === "" ? null : Number(form.purchaseValue),
        categoryId: form.categoryId || null,
        typeId: form.typeId || null,
      };
      if (isEdit) {
        await assetService.updateAsset(asset.id, payload);
        toast.success("Asset updated");
      } else {
        await assetService.createAsset(payload);
        toast.success("Asset created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-lg">
        <div className="flex items-center justify-between border-b border-[hsl(var(--ds-border))] px-4 py-3">
          <h2 className="ds-h3 text-[hsl(var(--ds-fg))]">
            {isEdit ? "Edit Asset" : "Add Asset"}
          </h2>
          <button type="button" onClick={onClose} className="rounded p-1 text-[hsl(var(--ds-muted-fg))] hover:bg-[hsl(var(--ds-muted))]">
            <X className="h-5 w-5" />
          </button>
        </div>
        <form className="grid grid-cols-1 gap-3 p-4 sm:grid-cols-2" onSubmit={handleSubmit}>
          {isEdit && (asset.tag || asset.assetTag || asset.asset_tag) ? (
            <div className="sm:col-span-2">
              <label className={fieldLabel}>Asset Tag</label>
              <input className={inputSm} value={asset.tag || asset.assetTag || asset.asset_tag} disabled />
            </div>
          ) : (
            <div className="sm:col-span-2">
              <p className="ds-caption text-[hsl(var(--ds-muted-fg))]">
                Tag will be auto-generated on save from numbering settings.
              </p>
            </div>
          )}
          <div className="sm:col-span-2">
            <label className={fieldLabel}>Name</label>
            <input className={inputSm} value={form.name} onChange={(e) => set("name", e.target.value)} required />
          </div>
          <div>
            <label className={fieldLabel}>Category</label>
            <select className={inputSm} value={form.categoryId} onChange={(e) => set("categoryId", e.target.value)}>
              <option value="">Select</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={fieldLabel}>Type</label>
            <select className={inputSm} value={form.typeId} onChange={(e) => set("typeId", e.target.value)}>
              <option value="">Select</option>
              {filteredTypes.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div className="flex items-end gap-2">
            <label className="flex items-center gap-2 text-sm text-[hsl(var(--ds-fg))]">
              <input
                type="checkbox"
                checked={form.isSerialized}
                onChange={(e) => set("isSerialized", e.target.checked)}
              />
              Serialized
            </label>
          </div>
          {form.isSerialized ? (
            <div>
              <label className={fieldLabel}>Serial Number</label>
              <input className={inputSm} value={form.serialNumber} onChange={(e) => set("serialNumber", e.target.value)} />
            </div>
          ) : (
            <div>
              <label className={fieldLabel}>Quantity</label>
              <input
                type="number"
                min={1}
                className={inputSm}
                value={form.quantity}
                onChange={(e) => set("quantity", e.target.value)}
              />
            </div>
          )}
          <div>
            <label className={fieldLabel}>Purchase Date</label>
            <input type="date" className={inputSm} value={form.purchaseDate} onChange={(e) => set("purchaseDate", e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>Purchase Value</label>
            <input type="number" min={0} step="0.01" className={inputSm} value={form.purchaseValue} onChange={(e) => set("purchaseValue", e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>Vendor</label>
            <input className={inputSm} value={form.vendor} onChange={(e) => set("vendor", e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>Location</label>
            <input className={inputSm} value={form.location} onChange={(e) => set("location", e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>Branch ID</label>
            <input className={inputSm} value={form.branchId} onChange={(e) => set("branchId", e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <label className={fieldLabel}>Department ID</label>
            <input className={inputSm} value={form.departmentId} onChange={(e) => set("departmentId", e.target.value)} placeholder="Optional" />
          </div>
          <div>
            <label className={fieldLabel}>Condition</label>
            <select className={inputSm} value={form.condition} onChange={(e) => set("condition", e.target.value)}>
              {ASSET_CONDITIONS.map((c) => (
                <option key={c.id} value={c.id}>{c.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={fieldLabel}>Warranty Expiry</label>
            <input type="date" className={inputSm} value={form.warrantyExpiry} onChange={(e) => set("warrantyExpiry", e.target.value)} />
          </div>
          <div>
            <label className={fieldLabel}>AMC Expiry</label>
            <input type="date" className={inputSm} value={form.amcExpiry} onChange={(e) => set("amcExpiry", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={fieldLabel}>Notes</label>
            <textarea className={inputSm} rows={2} value={form.notes} onChange={(e) => set("notes", e.target.value)} />
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2">
            <DsButton type="button" variant="outline" onClick={onClose}>Cancel</DsButton>
            <DsButton type="submit" disabled={saving}>{saving ? "Saving…" : "Save"}</DsButton>
          </div>
        </form>
      </div>
    </div>
  );
}
